import React from 'react';

export type TagOption = { id: number; slug: string; description: string | null };

interface TagMultiSelectProps {
    label: string;
    options: TagOption[];
    selectedIds: number[];
    onChange: (ids: number[]) => void;
    requireAtLeastOne?: boolean;
}

const TagMultiSelect: React.FC<TagMultiSelectProps> = ({
    label,
    options,
    selectedIds,
    onChange,
    requireAtLeastOne = true,
}) => {
    const allSelected = options.length > 0 && selectedIds.length === options.length;
    const noneSelected = selectedIds.length === 0;

    const toggle = (id: number) => {
        if (selectedIds.includes(id)) {
            onChange(selectedIds.filter(x => x !== id));
        } else {
            onChange([...selectedIds, id]);
        }
    };

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">{label}</label>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => onChange(options.map(o => o.id))}
                        disabled={allSelected || options.length === 0}
                        className="text-xs px-2 py-1 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Select all
                    </button>
                    <button
                        type="button"
                        onClick={() => onChange([])}
                        disabled={noneSelected}
                        className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Clear
                    </button>
                </div>
            </div>
            {options.length === 0 ? (
                <p className="text-xs text-gray-500 italic">No options available.</p>
            ) : (
                <div className="flex flex-wrap gap-2">
                    {options.map(option => {
                        const isSelected = selectedIds.includes(option.id);
                        return (
                            <button
                                key={option.id}
                                type="button"
                                onClick={() => toggle(option.id)}
                                title={option.description || option.slug}
                                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                    isSelected
                                        ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
                                        : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400 hover:bg-indigo-50'
                                }`}
                            >
                                {option.slug}
                            </button>
                        );
                    })}
                </div>
            )}
            {requireAtLeastOne && noneSelected && (
                <p className="text-xs text-red-600">At least one must be selected.</p>
            )}
        </div>
    );
};

export default TagMultiSelect;
